import { createTheme } from '@mui/material/styles';

const primaryMain = '#2B2F51';
const secondaryMain = '#8afcda';
const primaryMainDisabled = '#9597a8';
const secondaryMainDisabled = '#c6feed';
const secondaryLight = '#dcf7f4';
const secondaryLightDisabled = '#f2fffd';
const errorMain = '#c83c3c';
const successMain = '#097152';
const white = '#FFFFFF';
const gray = '#f5f5f5';
const darkGray = '#696F6E';
const darkerGray = '#4f5453';

const h6FontSize = 14;

export default function muiConfig() {
  return {
    theme: createTheme({
      palette: {
        primary: {
          main: primaryMain,
          disabled: primaryMainDisabled,
        },
        secondary: {
          main: secondaryMain,
          disabled: secondaryMainDisabled,
          light: secondaryLight,
          lightDisabled: secondaryLightDisabled,
        },
        error: {
          main: errorMain,
        },
        success: {
          main: successMain,
        },
        gray: {
          main: gray,
          dark: darkGray,
          darker: darkerGray,
        },
        white: {
          main: white,
        },
        text: {
          primary: primaryMain,
        },
      },
      typography: {
        fontFamily: 'Poppins',
      },
      components: {
        MuiContainer: {
          styleOverrides: {
            root: {
              margin: '20px',
              marginLeft: '22px',
              marginRight: '22px',
              padding: 0,
              paddingLeft: 0,
              paddingRight: 0,
              width: 'unset',
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              color: primaryMain,
              backgroundColor: secondaryLight,
            },
            arrow: {
              color: secondaryLight,
            },
          },
        },
        MuiTypography: {
          styleOverrides: {
            h1: {
              fontSize: '32px',
              fontWeight: '700',
              lineHeight: '48px',
              color: primaryMain,
            },
            h2: {
              fontSize: '24px',
              fontWeight: '700',
              lineHeight: '36px',
              color: primaryMain,
            },
            h3: {
              fontSize: '18px',
              fontWeight: '600',
              lineHeight: '27px',
              color: primaryMain,
            },
            h4: {
              fontSize: '16px',
              fontWeight: '600',
              lineHeight: '24px',
              color: primaryMain,
            },
            h5: {
              fontSize: '16px',
              fontWeight: '500',
              lineHeight: '24px',
              color: primaryMain,
            },
            h6: {
              fontSize: `${h6FontSize}px`,
              fontWeight: '600',
              lineHeight: '19.04px',
              color: primaryMain,
            },
            subtitle1: {
              fontSize: '20px',
              fontWeight: '700',
              letterSpacing: '0.16em',
              lineHeight: '30px',
              textTransform: 'uppercase',
            },
            subtitle2: {
              fontSize: '20px',
              fontWeight: '700',
              lineHeight: '30px',
            },
            body1: {
              fontSize: '16px',
              fontWeight: '400',
              lineHeight: '21.76px',
            },
            body2: {
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '16.8px',
            },
          },
        },
        MuiSvgIcon: {
          styleOverrides: {
            fontSizeSmall: {
              fontSize: '14px',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 'none',
              fontSize: '16px',
              fontWeight: '600',
              height: '40px',
              lineHeight: '24px',
              textTransform: 'none',
              width: '160px',
            },
            outlined: {
              border: `1px solid ${primaryMain}`,
            },
            contained: {
              color: '#fff',
              backgroundColor: primaryMain,
              '&:focus': {
                color: '#fff',
                backgroundColor: primaryMain,
              },
              '&:hover': {
                color: '#fff',
                backgroundColor: primaryMain,
                '@media (hover: none)': {
                  backgroundColor: primaryMain,
                },
              },
              '&:disabled': {
                color: '#fff',
                backgroundColor: primaryMainDisabled,
              },
            },
            containedPrimary: {
              backgroundColor: secondaryMain,
              color: primaryMain,
              '&:hover': {
                backgroundColor: secondaryMain,
                color: primaryMain,
                '@media (hover: none)': {
                  backgroundColor: `${secondaryMain} !important`,
                  color: `${primaryMain} !important`,
                },
              },
              '&:focus': {
                backgroundColor: secondaryMain,
                color: primaryMain,
              },
              '&:disabled': {
                backgroundColor: secondaryMainDisabled,
                color: primaryMainDisabled,
              },
            },
            containedSecondary: {
              backgroundColor: secondaryLight,
              color: primaryMain,
              '&:hover': {
                backgroundColor: secondaryLight,
                color: primaryMain,
              },
              '&:focus': {
                backgroundColor: secondaryLight,
                color: primaryMain,
              },
              '&:active': {
                backgroundColor: secondaryLight,
                color: primaryMain,
              },
              '&:disabled': {
                backgroundColor: secondaryLight,
                color: primaryMainDisabled,
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: '#fff',
            },
          },
        },
        MuiCardContent: {
          styleOverrides: {
            root: {
              '&:last-child': {
                paddingBottom: '16px',
              },
            },
          },
        },
        MuiListItemText: {
          styleOverrides: {
            primary: {
              fontSize: '18px',
              fontWeight: '600',
              lineHeight: '27px',
              color: '#fff'
            },
          },
        },
        MuiPickersTimePickerToolbar: {
          styleOverrides: {
            hourMinuteLabel: {
              alignItems: 'initial',
            },
            ampmLabel: {
              lineHeight: '32px',
            },
          },
        },
        MuiPickersToolbarButton: {
          styleOverrides: {
            toolbarBtn: {
              height: 'auto',
              width: 'auto',
            },
          },
        },
        MuiPickersToolbarText: {
          styleOverrides: {
            toolbarTxt: {
              fontSize: '60px',
              fontWeight: 500,
              lineHeight: '60px',
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            shrink: {
              fontSize: `${(h6FontSize * 4) / 3}px`,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              fontSize: `${(h6FontSize * 4) / 3}px`,
            },
          },
        },
      },
    }),
  };
}
